import { useMemo, useState } from "react";
import { useStyletron } from "baseui";
import { Modal, ModalBody, ModalButton, ModalFooter, ModalHeader } from "baseui/modal";
import {
  useAddResourceGroupMutation,
  useGetTenantsResourceGroupQuery,
} from "../../../services/resourceGroups/tenantApi";
import { useGetResourceGroupsQuery } from "../../../services/resourceGroups/resourceGroupApi";
import { LoadingView } from "../../../components/LoadingView";
import { ListItem, ListItemLabel } from "baseui/list";
import { VscSymbolClass } from "react-icons/vsc";
import { getUserId } from "../../../services/silentAuth";
import { AccessRoles, useHasRole } from "../../../hooks/useHasRole";
import { IResourceGroup } from "@oqdo/automationapps/Memberships/ResourceGroup";
import { useReportMutationState } from "../../../hooks/useReportMutationState";
import { Badge } from "baseui/badge";

interface LinkResourceGroupDialogProps {
  subscriptionId: string;
  tenantId: string;
  isOpen: boolean;
  setIsOpen: any;
}
function LinkResourceGroupDialog({ subscriptionId, tenantId, isOpen, setIsOpen }: LinkResourceGroupDialogProps) {
  const [selected, setSelected] = useState<IResourceGroup>();
  const [css, theme] = useStyletron();
  const [addRg, addRgResult] = useAddResourceGroupMutation();
  useReportMutationState(addRgResult, () => setIsOpen(false));
  function close() {
    setIsOpen(false);
  }

  function doAddRg() {
    if (selected) {
      addRg({
        resourceGroupId: selected.id,
        tenantId: tenantId,
        subscriptionId: subscriptionId,
      });
    }
  }

  return (
    <>
      <Modal onClose={close} isOpen={isOpen}>
        <ModalHeader>Link Resource Group</ModalHeader>
        <ModalBody>
          Select a ResouceGroup from the list to link
          <ResourceGroupsList
            selected={selected}
            setSelected={setSelected}
            subscriptionId={subscriptionId}
            tenantId={tenantId}
          />
        </ModalBody>
        <ModalFooter>
          <ModalButton kind="tertiary" onClick={close}>
            Cancel
          </ModalButton>
          <ModalButton disabled={!selected} isLoading={addRgResult.isLoading} onClick={doAddRg}>
            Okay
          </ModalButton>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default LinkResourceGroupDialog;

interface ResourceGroupsListProps {
  subscriptionId: string;
  tenantId: string;
  selected: any;
  setSelected: any;
}
const ResourceGroupsList = ({ subscriptionId, tenantId, selected, setSelected }: ResourceGroupsListProps) => {
  const [css, theme] = useStyletron();

  const userid = getUserId();
  const isSa = useHasRole(AccessRoles.SuperAdmin);
  const { data, isLoading, isSuccess, isError, error } = useGetResourceGroupsQuery({
    subscriptionId: isSa ? undefined : subscriptionId,
    page: 0,
    pageSize: 1000,
  });
  const { data: linkedRgs, isLoading: linkedRgsLoading } = useGetTenantsResourceGroupQuery(tenantId);

  const rgs = data?.data;
  const filtered = useMemo(() => {
    const linked = linkedRgs?.map((v) => v.id) ?? [];
    return rgs?.filter((v) => !linked.includes(v.id));
  }, [rgs, linkedRgs]);

  return (
    <LoadingView isLoading={isLoading || linkedRgsLoading} isSucess={isSuccess} error={error}>
      <ul
        className={css({
          paddingLeft: 0,
          paddingRight: 0,
          overflowY: "auto",
          maxHeight: "60vh",
        })}>
        {filtered?.map((item: IResourceGroup, index) => (
          <li
            key={item.id}
            onClick={() => {
              setSelected(item);
            }}>
            {selected === item ? (
              <ListItem overrides={selectedListItemStyle} artwork={() => <VscSymbolClass />} key={item.id}>
                <ListItemLabel>
                  {item.name} {item.disabled && <Badge content="DISABLED" color="primary" />}
                </ListItemLabel>
              </ListItem>
            ) : (
              <ListItem artwork={() => <VscSymbolClass />} key={item.id}>
                <ListItemLabel>
                  {item.name} {item.disabled && <Badge content="DISABLED" color="primary" />}
                </ListItemLabel>
              </ListItem>
            )}
          </li>
        ))}
      </ul>
    </LoadingView>
  );
};

const selectedListItemStyle = {
  Root: {
    style: ({ $theme }) => ({
      backgroundColor: $theme.colors.accent100,
    }),
  },
};
